/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description AgGrid table wrapper
 */
import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { Card, Pagination, Spin, Result, Flex, Checkbox } from 'antd';
import { CardProps } from 'antd/lib/card';
import {
	ClientSideRowModelModule,
	ColDef,
	GridApi,
	GridReadyEvent,
	ModuleRegistry,
	ValidationModule,
	ColumnAutoSizeModule,
	themeAlpine,
	CellStyleModule,
	RowAutoHeightModule,
	RowSelectionModule,
	RowApiModule,
	AllCommunityModule,
} from 'ag-grid-community';
import { PaginationProps } from 'antd/lib/pagination';

import { AppButton } from 'components/widgets/button';
import ColumnPersonalization from './ColumnPersonalization';
import { prepareColDef } from './ColumnPersonalization/utils';
import useWindowSize from 'hooks/useWindowSize';
import useUserAttributes, { getUserAttributes } from 'hooks/useUserAttributes';
import { useAppStoreValues } from 'store/app';
import { VARIABLES } from 'styles/theme';
import { getTotalSelectionCount } from 'utils/claims/workLists';

// default table selection meta
const defaultTableSelectionMeta: ITableSelectionMeta = {
	selectAll: false,
	selectedRows: [],
	deselectedRows: [],
	selectCount: 0,
	criteria: null,
};

// Register the module
ModuleRegistry.registerModules([
	ClientSideRowModelModule,
	ValidationModule,
	ColumnAutoSizeModule,
	CellStyleModule,
	RowAutoHeightModule,
	RowSelectionModule,
	RowApiModule,
	AllCommunityModule,
]);
// ag-grid theme
const gridTheme = themeAlpine.withParams({
	accentColor: VARIABLES.PRIMARY_COLOR,
	backgroundColor: VARIABLES.WHITE_COLOR,
	borderColor: VARIABLES.ICON_HOVER_BG,
	borderRadius: 2,
	browserColorScheme: 'light',
	columnBorder: true,
	columnHoverColor: '#2758CC00',
	foregroundColor: VARIABLES.TEXT_COLOR,
	fontFamily: 'Inter',
	headerFontSize: 14,
	headerFontWeight: 600,
	headerBackgroundColor: VARIABLES.TABLE_BG,
	headerRowBorder: true,
	iconSize: 14,
	oddRowBackgroundColor: '#F9FAFA',
	rowBorder: true,
	rowHoverColor: '#FFF1CF',
	spacing: 12,
	cellHorizontalPadding: 12,
	wrapperBorder: false,
	wrapperBorderRadius: 0,
	headerTextColor: VARIABLES.LABEL_COLOR,
	columnDropCellBackgroundColor: 'red',
	selectedRowBackgroundColor: '#FFF1CF',
});

// claim request table theme
const claimRequestTableTheme = themeAlpine.withParams({
	borderColor: VARIABLES.ICON_HOVER_BG,
	borderRadius: 2,
	browserColorScheme: 'light',
	columnBorder: true,
	headerFontSize: 14,
	cellHorizontalPadding: 4,
	headerRowBorder: true,
	textColor: '#8C8C8C',
	headerFontWeight: 400,
	iconSize: 14,
	rowBorder: true,
	spacing: 12,
	wrapperBorder: false,
	wrapperBorderRadius: 0,
	rowHoverColor: 'unset',
	headerBackgroundColor: 'unset',
	headerColumnResizeHandleWidth: 1,
	headerColumnResizeHandleHeight: '100%',
	accentColor: '#f0f0f0',
});

const ROW_HEIGHT = 46;
const HEADER_HEIGHT = 40;

// render pagination total
const renderPaginationTotal = (totalRows: number, range: [number, number]): React.ReactNode => {
	return `${range?.[0]}-${range?.[1]} of ${totalRows} items`;
};

export interface IAgGridTableProps extends AgGridReactProps {
	columnDefs: ColDef[];
	loading?: boolean;
	className?: string;
	defaultColDef?: AnyRecord;
	gridRef?: React.RefObject<AgGridReact>;
	headerFilter?: CardProps['title'];
	// component in the right side of the header
	headerExtra?: React.ReactNode[];
	paginationProps?: PaginationProps;
	isError?: boolean;
	reload?: VoidFunction;
	// custom
	personalizationKey?: string;
	tableOffset?: number;
	customNoRowsOverlay?: () => React.ReactNode;
	'data-testid'?: string;

	// row selection props
	selectionProps?: {
		data: { count: number; data: AnyType[] };
		tableKey: string;
		tableSelectionMeta: ITableSelectionMeta;
		setTableSelectionMeta: (tableSelectionMeta: ITableSelectionMeta) => void;
	};
}

const NO_ROWS_MESSAGE = {
	claims: 'No claims available.',
};

const LoadingOverlay: React.FC = () => {
	return (
		<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<Spin />
		</div>
	);
};

/**
 * AgGrid table wrapper
 * @param props
 * @returns {React.FC} AgGridTable
 */
const AgGridTable: React.FC<IAgGridTableProps> = (props) => {
	const {
		columnDefs,
		rowData,
		className,
		gridRef,
		defaultColDef,
		headerFilter,
		headerExtra,
		paginationProps,
		isError,
		reload,
		loading,
		personalizationKey,
		tableOffset,
		selectionProps,
		onGridReady: onGridReadyProp,
		'data-testid': dataTestId,
		getRowHeight,
		...restProps
	} = props;
	const { windowHeight } = useWindowSize();
	const { user } = useAppStoreValues();
	const userAttributes = useUserAttributes(user.id);
	const { data, tableKey, tableSelectionMeta, setTableSelectionMeta } = selectionProps || {};
	const gridApiRef = useRef<GridApi | null>(null);

	const { colDef, columnMeta } = useMemo(() => {
		// TODO check user attribute data empty also
		if (personalizationKey && !userAttributes.isLoading) {
			return prepareColDef({ colDef: columnDefs, columnMeta: getUserAttributes(personalizationKey, userAttributes.data) });
		}
		return { colDef: columnDefs, columnMeta: [] };
	}, [columnDefs, personalizationKey, userAttributes.isLoading, userAttributes.data]);

	const syncOverlayState = (api: GridApi) => {
		if (loading) {
			return;
		}
		if (isError) {
			api.hideOverlay();
			return;
		}
		if (_.size(rowData) === 0) {
			api.showNoRowsOverlay();
			return;
		}
		api.hideOverlay();
	};

	const onGridReady = (params: GridReadyEvent) => {
		gridApiRef.current = params.api;
		setTimeout(() => {
			params.api.sizeColumnsToFit();
		}, 0);
		syncOverlayState(params.api);
		onGridReadyProp?.(params);
	};

	const defaultColumnDef = useMemo(() => {
		return {
			resizable: true,
			sortable: false,
			...defaultColDef,
		};
	}, [rowData]);

	/* ----------------------------- table selection logic starts here ----------------------------- */

	// remove selected nodes when table selection meta is reset
	useEffect(() => {
		if (selectionProps) {
			const shouldRemoveSelectedNodes =
				!tableSelectionMeta?.selectAll &&
				_.isEmpty(tableSelectionMeta?.selectedRows) &&
				_.isEmpty(tableSelectionMeta?.deselectedRows);
			if (gridRef?.current?.api && shouldRemoveSelectedNodes) {
				gridRef.current.api.forEachNode((node) => {
					node.setSelected(false);
				});
			}
		}
	}, [tableSelectionMeta]);

	// when table data changes by pagination or any mean, change node selection state as per condition
	useEffect(() => {
		if (gridRef?.current?.api && data?.data?.length) {
			if (tableSelectionMeta?.selectAll) {
				if (_.isEmpty(tableSelectionMeta?.deselectedRows)) {
					gridRef.current.api.forEachNode((node) => {
						node.setSelected(true);
					});
				} else {
					gridRef.current.api.forEachNode((node) => {
						if (_.includes(tableSelectionMeta?.deselectedRows, _.get(node.data, tableKey))) {
							node.setSelected(false);
						} else {
							node.setSelected(true);
						}
					});
				}
			} else {
				gridRef.current.api.forEachNode((node) => {
					if (_.includes(tableSelectionMeta?.selectedRows, _.get(node.data, tableKey))) {
						node.setSelected(true);
					}
				});
			}
		}
	}, [data?.data]);

	// get total selection count for manual worklist from table selection meta
	const totalSelectionCount = useMemo(() => getTotalSelectionCount(tableSelectionMeta, data?.count), [tableSelectionMeta, data?.count]);

	// check if all rows are selected
	const isAllSelected = totalSelectionCount === data?.count && data?.count > 0;

	// on selection changed, update table selection meta
	const onSelectionChanged = () => {
		const currentIdsList = _.map(data?.data, tableKey);
		const selectedIdsList = _.map(gridRef.current?.api?.getSelectedRows(), tableKey);
		const selectableRows = [];
		gridRef.current?.api?.forEachNode((node) => {
			if (node.selectable) {
				selectableRows.push(node?.data?.[tableKey]);
			}
		});

		if (tableSelectionMeta?.selectAll) {
			const deselectedIdsList = _.difference(selectableRows, selectedIdsList);
			const existingDeselected = _.difference(tableSelectionMeta?.deselectedRows || [], selectedIdsList);
			const updatedDeselectedRows = _.uniq([...existingDeselected, ...deselectedIdsList]);
			setTableSelectionMeta({
				...tableSelectionMeta,
				deselectedRows: updatedDeselectedRows,
				selectCount: (data?.count || 0) - updatedDeselectedRows.length,
			});
		} else {
			const selectedList = _.intersection(selectedIdsList, currentIdsList);
			const existingSelected = _.difference(tableSelectionMeta?.selectedRows || [], currentIdsList);
			const updatedSelectedRows = _.uniq([...existingSelected, ...selectedList]);
			setTableSelectionMeta({
				...tableSelectionMeta,
				selectedRows: updatedSelectedRows,
				selectCount: updatedSelectedRows?.length,
			});
		}
	};

	// handle select all checkbox
	const handleSelectAll = (allSelected: boolean) => {
		setTableSelectionMeta({
			...defaultTableSelectionMeta,
			criteria: tableSelectionMeta?.criteria,
			selectAll: allSelected,
			selectCount: allSelected ? data?.count || 0 : 0,
		});
		if (gridRef?.current?.api && data?.data?.length) {
			gridRef.current.api.forEachNode((node) => {
				node.setSelected(allSelected);
			});
		}
	};

	// row selection props with custom header
	const rowSelectionProps: Partial<AgGridReactProps> = useMemo(() => {
		const indeterminate = totalSelectionCount > 0 && totalSelectionCount < data?.count;
		if (!_.isEmpty(selectionProps)) {
			return {
				selectionColumnDef: {
					width: 40,
					pinned: 'left',
					suppressSizeToFit: true,
					headerComponent: () => {
						if (data?.count === 0) {
							return null;
						}
						return (
							<Checkbox
								checked={isAllSelected}
								indeterminate={indeterminate}
								className='ag-grid-custom-checkbox'
								onChange={() => {
									if (tableSelectionMeta?.selectAll && isAllSelected) {
										handleSelectAll(false);
									} else if (!tableSelectionMeta?.selectAll && isAllSelected) {
										handleSelectAll(false);
										setTableSelectionMeta({
											...tableSelectionMeta,
											selectAll: true,
										});
									} else {
										handleSelectAll(true);
									}
								}}
							/>
						);
					},
				},
				onSelectionChanged,
				rowSelection: { mode: 'multiRow' },
			};
		}
		return {};
	}, [selectionProps, tableSelectionMeta?.selectAll, isAllSelected]);

	/* ----------------------------- table selection logic ends here ----------------------------- */

	const getExtra = () => {
		if (personalizationKey) {
			return (
				<Flex gap={8}>
					{_.compact([
						...(headerExtra || []),
						<ColumnPersonalization
							key='column-personalization'
							personalizationKey={personalizationKey}
							columnMeta={columnMeta}
						/>,
					])}
				</Flex>
			);
		}
		if (!_.isEmpty(headerExtra)) {
			return <Flex gap={8}>{headerExtra}</Flex>;
		}
		return null;
	};

	const getActions = () => {
		const paginationComponent = (
			<Pagination {...paginationProps} align='end' showSizeChanger={true} showQuickJumper showTotal={renderPaginationTotal} />
		);
		const paginationTestId = dataTestId ? `${dataTestId}-pagination` : undefined;
		if (rowData?.length) {
			if (paginationProps && selectionProps && data?.count > 0) {
				return [
					<div
						key='pagination'
						data-testid={paginationTestId}
						style={{
							margin: '0px 16px',
							textAlign: 'right',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<span style={{ color: '#000000d9' }}>{`Selected ${totalSelectionCount} of ${paginationProps?.total} items`}</span>
						{paginationComponent}
					</div>,
				];
			} else if (paginationProps && rowData?.length > 0) {
				return [
					<div key='pagination' data-testid={paginationTestId} style={{ marginRight: 16, textAlign: 'right' }}>
						{paginationComponent}
					</div>,
				];
			}
		}
		return null;
	};

	const showErrorMessage = !loading && isError;
	const rowHeight = props?.rowHeight || ROW_HEIGHT;
	const headerHeight = props?.headerHeight || HEADER_HEIGHT;
	const groupHeaderHeight = props?.groupHeaderHeight || 0;

	const containerProps: AgGridReactProps = useMemo(() => {
		if (!tableOffset) {
			return { domLayout: 'autoHeight' };
		}
		const availableHeight = windowHeight - (tableOffset || 0);
		const rows = _.size(rowData);
		if (showErrorMessage) {
			return { containerStyle: { height: headerHeight + groupHeaderHeight } };
		}
		if (!rows) {
			return { containerStyle: { height: availableHeight } };
		}
		const totalContentHeight = getRowHeight
			? _.sumBy(rowData as AnyType[], (row) => (getRowHeight as AnyType)({ data: row }) ?? rowHeight)
			: rows * rowHeight;
		return { containerStyle: { height: _.min([availableHeight - 1, totalContentHeight + headerHeight + groupHeaderHeight + 1]) } };
	}, [showErrorMessage, rowData, loading, rowHeight, headerHeight, windowHeight, tableOffset, groupHeaderHeight, getRowHeight]);

	useEffect(() => {
		if (!gridApiRef.current) {
			return;
		}
		syncOverlayState(gridApiRef.current);
	}, [loading, isError, rowData]);

	return (
		<Card
			data-testid={dataTestId}
			className={className}
			rootClassName='ag-grid-card-container'
			title={headerFilter}
			actions={getActions()}
			extra={getExtra()}
		>
			<AgGridReact
				ref={gridRef}
				theme={gridTheme}
				rowHeight={rowHeight}
				headerHeight={headerHeight}
				loading={!!loading}
				loadingOverlayComponent={LoadingOverlay}
				pagination={false}
				animateRows={true}
				suppressMovableColumns={true}
				suppressMenuHide={true}
				suppressContextMenu={true}
				enableCellTextSelection={true}
				rowData={showErrorMessage || loading ? [] : rowData}
				columnDefs={colDef}
				onGridReady={onGridReady}
				defaultColDef={defaultColumnDef}
				suppressNoRowsOverlay={!!isError || !!loading}
				getRowHeight={getRowHeight}
				{...rowSelectionProps}
				{...containerProps}
				{...restProps}
			/>
			{showErrorMessage && (
				<Result
					status='warning'
					title='Oops!'
					subTitle='Something went wrong! Please try after sometime.'
					extra={[<AppButton.Reload key='reload' onClick={reload} />]}
				/>
			)}
		</Card>
	);
};

export { claimRequestTableTheme, defaultTableSelectionMeta, renderPaginationTotal, NO_ROWS_MESSAGE };
export default AgGridTable;
